import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CalendarClock, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import ActionModalHeader from '@/components/ui/ActionModalHeader';
import { cn } from '@/lib/utils';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { formatScheduleDate } from '@/utils/formatScheduleDate';
import { fmtCurrency } from '@/utils/money';
import {
  ACTION_MODAL_CONTENT_CLASS,
  ACTION_MODAL_BODY_CLASS,
  MODAL_PRIMARY_BTN_CLASS,
  MODAL_GHOST_BTN_CLASS,
} from '@/components/booking/paymentScheduleModalStyles';

/**
 * "Can't make this payment?" (founder 2026-10-25): the customer picks the day
 * they'll be able to pay, up to 14 days later, for a $35 (up to 7 days) or
 * $50 (8 to 14) Extension Fee spread over their remaining payments. Nothing
 * is charged today. Every rail's update-schedule takes `extension.to_date`
 * (supabase/functions/_shared/payment-extension.ts); the screen shows the
 * server's own dry run before the customer agrees, and the fee is only
 * charged with `pay_extension_fee: true`.
 */

const usd = (cents) => `$${fmtCurrency(cents)}`;

async function readJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { error: res.status === 429 ? 'Too many requests. Please try again in a few minutes.' : 'Something went wrong. Please try again.' };
  }
}

async function postExtension({ apiUrlFn, tripId, paymentFrequency, toDate, dryRun, requestId, expected }) {
  const { supabase } = await import('@/lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Please sign in again.');
  const res = await fetch(apiUrlFn('update-schedule'), {
    method: 'POST',
    headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
    body: JSON.stringify({
      booking_id: tripId,
      payment_frequency: paymentFrequency,
      extension: { to_date: toDate },
      /** Confirm the payment and fee the preview showed: if either changed, nothing moves. */
      ...(dryRun
        ? { dry_run: true }
        : { pay_extension_fee: true, client_request_id: requestId, expected_from_date: expected?.from_date, expected_fee_cents: expected?.fee_cents }),
    }),
  });
  const data = await readJson(res);
  return { ok: res.ok, status: res.status, data };
}

export default function MovePaymentDialog({
  open,
  onOpenChange,
  tripId,
  apiUrlFn,
  paymentFrequency,
  choices,
  dueDate,
  dueAmountCents = 0,
  failed = false,
  onMoved,
}) {
  const [toDate, setToDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setToDate('');
      setPreview(null);
      setPreviewError('');
      setAgreed(false);
      requestIdRef.current = null;
    }
  }, [open]);

  /** The server's dry run for the day picked: the new dates and amounts, and the fee it will charge. */
  useEffect(() => {
    if (!open || !toDate || !tripId) return undefined;
    let stale = false;
    setLoadingPreview(true);
    setPreview(null);
    setPreviewError('');
    setAgreed(false);
    postExtension({ apiUrlFn, tripId, paymentFrequency, toDate, dryRun: true })
      .then(({ ok, data }) => {
        if (stale) return;
        if (!ok || !data?.extension) setPreviewError(data?.error || 'We couldn’t work that date out. Please pick another.');
        else setPreview(data);
      })
      .catch((e) => { if (!stale) setPreviewError(e.message || 'We couldn’t work that date out. Please try again.'); })
      .finally(() => { if (!stale) setLoadingPreview(false); });
    return () => { stale = true; };
  }, [open, toDate, tripId, apiUrlFn, paymentFrequency]);

  const ext = preview?.extension || null;
  const previewMatches = Boolean(ext && ext.to_date === toDate);
  const feeCents = ext?.fee_cents ?? 0;
  /** Payments must finish 5 days before departure, so a later date can leave
   *  fewer of them, each bigger (WISNQZ: $335.28 → $520.42). Say so plainly. */
  const fewerFit = Boolean(preview) && (
    Number(preview.folded_payments) > 0
    || (Array.isArray(preview.warnings) && preview.warnings.some((w) => /per-payment amount rises/.test(String(w))))
  );

  const submit = async () => {
    if (!previewMatches || !agreed || saving) return;
    if (!requestIdRef.current) {
      requestIdRef.current = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    setSaving(true);
    try {
      const { ok, data } = await postExtension({ apiUrlFn, tripId, paymentFrequency, toDate, dryRun: false, requestId: requestIdRef.current, expected: ext });
      if (!ok) {
        toast.error(data?.error || 'We couldn’t move your payment. Nothing was changed.');
        /** The schedule moved under us: refresh it so the card shows what's true now. */
        if (['schedule_drift', 'installment_processing', 'extension_changed', 'payment_processing', 'payment_in_progress', 'payment_already_extended', 'extension_limit_reached', 'card_needs_update'].includes(data?.code)) {
          onOpenChange(false);
          onMoved?.(null);
        }
        return;
      }
      /** A retried confirm that had already gone through comes back as a replay: same outcome. */
      const movedTo = data?.extension?.to_date || toDate;
      toast.success(`Done. Your payment is now on ${formatScheduleDate(movedTo, 'EEE, MMM d')}.`);
      onOpenChange(false);
      onMoved?.({ to_date: movedTo, due_date: data?.extension?.from_date || null });
    } catch {
      /** No answer came back, so the move may or may not have gone through. Refresh
       *  rather than guess; trying again sends the same request, which reads as done
       *  if it went through. */
      toast.error('We couldn’t reach Defera to confirm. Your schedule is refreshing: check it before trying again.');
      onMoved?.(null);
    } finally {
      setSaving(false);
    }
  };

  if (!choices?.available) return null;
  const maxDays = choices.tiers[choices.tiers.length - 1]?.maxDays;
  /** A failed payment counts from today; when a retry is already booked, only the days after it. */
  const description = failed && choices.retryDate
    ? `Missed a payment? We’ll try it again on ${formatScheduleDate(choices.retryDate, 'EEE, MMM d')}. Or pick a later day you’ll be able to pay, up to ${maxDays} days from today.`
    : failed
      ? `Missed a payment? Pick the day you’ll be able to pay, up to ${maxDays} days from today. Nothing is charged today.`
      : `Can’t make your ${formatScheduleDate(dueDate, 'EEE, MMM d')} payment${dueAmountCents > 0 ? ` of ${usd(dueAmountCents)}` : ''}? Pick the day you’ll be able to pay, up to ${maxDays} days later.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={ACTION_MODAL_CONTENT_CLASS}>
        <ActionModalHeader variant="dialog" icon={CalendarClock} title="When can you pay?" description={description} />
        <div className={cn(ACTION_MODAL_BODY_CLASS, 'space-y-5')}>
          {choices.tiers.map((tier) => (
            <div key={tier.maxDays} className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-white/80 text-xs font-medium">
                  {tier.minDays === 1 ? `Up to ${tier.maxDays} days later` : `${tier.minDays} to ${tier.maxDays} days later`}
                </p>
                <span className="text-white/60 text-xs font-normal tabular-nums">{usd(tier.feeCents)} fee</span>
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {tier.days.map((d) => {
                  const selected = d.date === toDate;
                  return (
                    <button
                      key={d.date}
                      type="button"
                      disabled={!d.allowed || saving}
                      onClick={() => setToDate(d.date)}
                      aria-pressed={selected}
                      aria-label={`${formatScheduleDate(d.date, 'EEEE, MMMM d')}, ${usd(tier.feeCents)} fee`}
                      className={cn(
                        'flex flex-col items-center justify-center rounded-xl border py-2 transition-colors touch-manipulation',
                        selected
                          ? 'bg-brand-800 border-brand-600 text-white shadow-[0_0_16px_-6px_rgba(10,18,51,0.7)]'
                          : 'bg-white/[0.03] border-white/10 text-white/80 hover:border-white/25 hover:text-white',
                        !d.allowed && 'opacity-30 cursor-not-allowed hover:border-white/10',
                      )}
                    >
                      <span className="text-[10px] uppercase tracking-wide opacity-70">{formatScheduleDate(d.date, 'EEE')}</span>
                      <span className="text-sm font-medium tabular-nums leading-tight">{formatScheduleDate(d.date, 'd')}</span>
                      <span className="text-[10px] opacity-60">{formatScheduleDate(d.date, 'MMM')}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {choices.tiers.some((t) => t.days.some((d) => !d.allowed)) && (
            <p className="text-white/50 text-[11px] leading-snug">
              {choices.retryDate
                ? `Greyed-out days are on or before ${formatScheduleDate(choices.retryDate, 'MMM d')}, when we’ll try again, or too close to your trip.`
                : 'Greyed-out days are too close to your trip: payments finish 5 days before departure.'}
            </p>
          )}

          {toDate && loadingPreview && (
            <div className="flex items-center gap-2 text-white/60 text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Working out your new schedule…
            </div>
          )}
          {toDate && previewError && !loadingPreview && (
            <div className="flex items-start gap-2 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-2.5 text-rose-100 text-xs leading-snug">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {previewError}
            </div>
          )}
          {previewMatches && !loadingPreview && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-white/60 text-xs">{ext.past_due ? 'You’ll pay on' : 'Your payment moves to'}</span>
                <span className="text-white text-sm font-medium">{formatScheduleDate(ext.to_date, 'EEE, MMM d')}</span>
              </div>
              {Number(preview.payment_amount_cents) > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-white/60 text-xs">Next payment</span>
                  <span className="text-white text-sm font-medium tabular-nums">
                    {usd(preview.payment_amount_cents)}
                    {Number(ext.per_payment_before_cents) > 0 && Number(ext.per_payment_before_cents) !== Number(preview.payment_amount_cents) && (
                      <span className="text-white/45 font-normal"> (was {usd(ext.per_payment_before_cents)})</span>
                    )}
                  </span>
                </div>
              )}
              {Number(preview.payments_remaining) > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-white/60 text-xs">Payments left</span>
                  <span className="text-white text-sm font-medium tabular-nums">
                    {preview.payments_remaining}
                    {preview.last_payment_date && preview.payments_remaining > 1 && (
                      <span className="text-white/45 font-normal">, last on {formatScheduleDate(preview.last_payment_date, 'MMM d')}</span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="text-white/60 text-xs">Charged today</span>
                <span className="text-white text-sm font-medium tabular-nums">$0.00</span>
              </div>
              {ext.past_due && (
                <p className="text-white/55 text-[11px] leading-snug pt-1">Your missed payment stops retrying, and your trip is back on track.</p>
              )}
            </div>
          )}
          {previewMatches && !loadingPreview && fewerFit && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2.5 text-amber-100 text-xs leading-snug">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {Number(preview.folded_payments) > 0
                  ? `Your trip is close, so fewer payments fit before it: you'll have ${preview.payments_remaining} left, and ${preview.payments_remaining === 1 ? 'that one is' : 'the later ones are'} bigger.`
                  : 'Fewer payments fit before your trip, so each payment is bigger.'}
                {' '}An earlier day may keep them smaller.
              </span>
            </div>
          )}
          {previewMatches && !loadingPreview && Array.isArray(preview.warnings) && preview.warnings.some((w) => /drafted/.test(w)) && (
            <p className="text-amber-200/80 text-[11px] leading-snug">A payment is being processed on this trip right now. Try again in about an hour.</p>
          )}

          {previewMatches && !loadingPreview && (
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-brand-500 focus:ring-brand-500/30"
              />
              <span className="text-white/75 text-xs leading-snug">
                I agree to the {usd(feeCents)} Extension Fee. It&apos;s spread across my remaining payments and isn&apos;t refundable: if I cancel, it comes out of what I&apos;ve paid.
              </span>
            </label>
          )}
          <p className="text-white/45 text-[11px] leading-snug">
            {choices.left > 1
              ? `You can move a payment ${choices.left} times on this trip, and each payment once.`
              : 'This is the last time you can move a payment on this trip.'}
          </p>
        </div>
        <div className="flex-shrink-0 border-t border-white/[0.06] px-4 py-3 sm:px-6 grid grid-cols-2 gap-2">
          <Button type="button" variant="ghost" className={MODAL_GHOST_BTN_CLASS} onClick={() => onOpenChange(false)} disabled={saving}>
            Not now
          </Button>
          <Button type="button" className={MODAL_PRIMARY_BTN_CLASS} onClick={submit} disabled={!previewMatches || !agreed || saving || loadingPreview}>
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Moving…</> : 'Move my payment'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

MovePaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  tripId: PropTypes.string,
  apiUrlFn: PropTypes.func.isRequired,
  paymentFrequency: PropTypes.oneOf(['weekly', 'biweekly']).isRequired,
  choices: PropTypes.shape({
    available: PropTypes.bool,
    left: PropTypes.number,
    retryDate: PropTypes.string,
    tiers: PropTypes.arrayOf(PropTypes.shape({
      minDays: PropTypes.number,
      maxDays: PropTypes.number,
      feeCents: PropTypes.number,
      days: PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string, days: PropTypes.number, allowed: PropTypes.bool })),
    })),
  }),
  dueDate: PropTypes.string,
  dueAmountCents: PropTypes.number,
  failed: PropTypes.bool,
  onMoved: PropTypes.func,
};
